import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Checkbox,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../../../common/intl';
import { companySelectors } from '../../../../../state/ducks/company';
import { DocumentRevision } from '../../../../../state/ducks/documentRevisions/types';
import { AlertDialog } from '../../../../components/dialogs';
import { Autocomplete } from '../../../../components/forms/fields-next';
import { OptionType } from '../../../../components/forms/fields/Autocomplete/types';
import useDialog from '../../../../hooks/useDialog';
import useHover from '../../../../hooks/useHover';
import Colors from '../../../../layout/theme/utils/colors';
import { LotTransferType } from '../../../FBLotTransfers/types';
import { useAllowedLotTransferOptions } from '../../../FBLotTransfers/utils';
import { FB } from '../../../helpers';
import { AllocationCheckBoxOptions, AUTO_TRANSACT_TO_LOCATION } from './constants';

const styles = makeStyles((theme) => ({
  checkbox: {
    color: Colors.font_gray,
    padding: theme.spacing(0.8),
    '&$checked': {
      color: Colors.navy,
    },
  },
  checked: {
    color: Colors.navy,
  },
  disabled: {
    opacity: 0.5,
  },
  label: {
    fontWeight: 400,
    fontSize: 14,
    color: Colors.almost_black,
  },
  icon: {
    height: 17,
    width: 17,
    marginLeft: theme.spacing(0.88),
  },
  autocompleteLabel: {
    marginLeft: theme.spacing(1.5),
  },
  inputRoot: {
    width: theme.spacing(40),
    padding: `${theme.spacing(0.25, 0.25, 0.25, 0.5)} !important`,
    minHeight: 0,
  },
}));

interface AutoOptionsProps {
  onAutoAssign: (isAutoAssign: boolean) => void
  isDisabled: boolean
  documentRevision: DocumentRevision
  isDocument: boolean
}

const AutoOptions: React.FC<AutoOptionsProps> = ({
  onAutoAssign,
  isDisabled,
  documentRevision,
  isDocument,
}) => {
  const { formState, workspaceState } = FB.useStores();
  const classes = styles();
  const confirmationDialog = useDialog();
  const [autoAssign, setAutoAssign] = useState<boolean>(
    workspaceState?.formInput?.autoAssign ?? documentRevision?.formInput?.autoAssign ?? false,
  );
  const [tansactIconRef, isHovered] = useHover<HTMLButtonElement>();
  const [autoTransact, setAutoTransact] = useState<boolean>(
    workspaceState?.formInput?.autoTransact ?? documentRevision?.formInput?.autoTransact ?? false,
  );

  const [toLocationRef, isToLocationHovered] = useHover<HTMLButtonElement>();
  const [autoTransactToLocation, setAutoTransactToLocation] = useState<string>(
    workspaceState?.formInput?.autoTransactToLocation ?? documentRevision?.formInput?.autoTransactToLocation ?? '',
  );
  const locations = useSelector(companySelectors.getAllLocations) ?? [];
  const isMaterialFlowAclEnabled = useSelector(companySelectors.isMaterialFlowAclEnabled);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [_, allowedOptions] = useAllowedLotTransferOptions({ type: LotTransferType.Allocate });
  let toLocations = locations
    .filter(location => location.active);

  if (isMaterialFlowAclEnabled) {
    toLocations = toLocations.filter(location => allowedOptions.to.includes(location.id));
  }
  const toLocationOptions: OptionType[] = toLocations.map((location) => ({ label: location.name, value: location.id }));

  const onAutoAssignChange = (value: boolean) => {
    setAutoAssign(value);
    formState?.setFieldValue(AllocationCheckBoxOptions.AUTO_ASSIGN, value);
    onAutoAssign?.(value);
    workspaceState?.saveDocRev({
      ...workspaceState?.formInput,
      autoAssign: value,
    });
  };

  const onAutoTransactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isAutoTransactEnabled = event.target.checked;
    setAutoTransact(isAutoTransactEnabled);
    formState?.setFieldValue(AllocationCheckBoxOptions.AUTO_TRANSACT, isAutoTransactEnabled);
    if (!isAutoTransactEnabled) {
      formState?.setFieldValue(AUTO_TRANSACT_TO_LOCATION, undefined);
      setAutoTransactToLocation('');
    }
    workspaceState?.saveDocRev({
      ...workspaceState?.formInput,
      autoTransact: isAutoTransactEnabled,
      [AUTO_TRANSACT_TO_LOCATION]: undefined,
    });
  };

  const onAutoAssignConfirm = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      confirmationDialog.open();
    } else {
      onAutoAssignChange(event.target.checked);
    }
  };

  const onToLocationSelected = (optionSelected: OptionType) => {
    const selectedToLocation = optionSelected?.value ?? undefined;
    setAutoTransactToLocation(selectedToLocation);
    formState?.setFieldValue(AUTO_TRANSACT_TO_LOCATION, selectedToLocation);
    workspaceState?.saveDocRev({
      ...workspaceState?.formInput,
      [AUTO_TRANSACT_TO_LOCATION]: selectedToLocation,
    });
  };

  const onConfirmAlertDialog = () => {
    onAutoAssignChange(true);
    confirmationDialog.close();
  };

  return (
    <>
      <FormControlLabel
        className={classes.checkbox}
        control={
          <Checkbox
            checked={autoAssign}
            classes={{
              root: classes.checkbox,
              checked: classes.checked,
              disabled: classes.disabled,
            }}
            disabled={isDisabled}
            disableRipple
            onChange={onAutoAssignConfirm}
            name="autoAssign"
          />
        }
        label={
          <Typography variant="body2" className={classes.label}>
            {translate('allocation.checkbox.group.option.autoAssign.label')}
          </Typography>
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={autoTransact}
            classes={{
              root: classes.checkbox,
              checked: classes.checked,
              disabled: classes.disabled,
            }}
            disabled={isDisabled}
            disableRipple
            onChange={onAutoTransactChange}
            name="autoTransact"
          />
        }
        label={
          <Typography variant="body2" className={classes.label}>
            {translate('allocation.checkbox.group.option.autoTransact.label')}
            <Tooltip
              title={translate('allocation.checkbox.group.option.autoTransact.title.message')}
              placement="right"
              arrow
            >
              <span ref={tansactIconRef}>
                <FontAwesomeIcon
                  data-cy="auto-transact-icon"
                  className={classes.icon}
                  color={Colors.navy}
                  icon={isHovered ? solid('circle-exclamation') : regular('circle-exclamation')}
                />
              </span>
            </Tooltip>
          </Typography>
        }
      />
      {
        (autoTransact || isDocument)
        && <FormControlLabel
          className={classes.autocompleteLabel}
          control={
            <Autocomplete
              options={toLocationOptions}
              placeholder={translate('allocation.autoTransact.transfer.to.location.msg')}
              getOptionLabel={(option: OptionType) => option.label }
              data-cy="default-to-location-selection"
              value={toLocationOptions.find((option) => option.value === autoTransactToLocation) || null}
              onChange={(_, selectedOption) => onToLocationSelected(selectedOption)}
              className={classes.inputRoot}
              disabled={isDisabled}
            />
          }
          label={
            <Tooltip
              title={translate('allocation.autoTransact.transfer.to.location.tooltip.message')}
              placement="right"
              arrow
            >
              <span ref={toLocationRef}>
                <FontAwesomeIcon
                  data-cy="auto-transact-icon"
                  className={classes.icon}
                  color={Colors.navy}
                  icon={isToLocationHovered ? solid('circle-exclamation') : regular('circle-exclamation')}
                />
              </span>
            </Tooltip>
          }
        />
      }
      {confirmationDialog.isOpen && (
        <AlertDialog
          handler={confirmationDialog}
          onConfirm={onConfirmAlertDialog}
          onCancel={confirmationDialog.close}
          title="common.warning"
          confirmLabel="common.yes.continue"
          cancelLabel="common.no.go.back"
          warning
        >
          {translate('allocation.checkbox.group.option.autoAssign.enable.popup.message')}
        </AlertDialog>
      )}
    </>
  );
};
export default AutoOptions;
